/* eslint-disable camelcase */
import { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import { toLocalDateString } from '../../../../utils/dateUtils';
import { selectCurrentEmpid } from '../../../../pages/login/authSlice';
import {
    useGetSessionByIdQuery,
    useSaveFuelNotesMutation} from '../../coachingActionSlice';
import {
    useGetCoachingActionQuery
} from '../../../../components/skillBehaviorDropdown/skillBehaviorDropdownSlice';

/**
 * Custom hook for managing FUEL notes state
 * Notes are saved only when user clicks "Start Action Plan" button
 */
export const useFuelNotes = () => {
    const [frameNotes, setFrameNotes] = useState('');
    const [understandNotes, setUnderstandNotes] = useState('');
    const [exploreNotes, setExploreNotes] = useState('');

    const [saveFuelNotes] = useSaveFuelNotesMutation();

    return {
        frameNotes,
        setFrameNotes,
        understandNotes,
        setUnderstandNotes,
        exploreNotes,
        setExploreNotes,
        saveFuelNotes
    };
};

/**
 * Custom hook for managing Layout form state.
 *
 * Default follow-up date (next Monday) is set only when:
 *   - followUpDate is still null AND
 *   - session data has already been loaded (isDataLoaded = true) with no date
 *
 * This prevents the default from overriding an existing follow_up_date
 * loaded from the session record.
 */
export const useLayoutForm = () => {
    const [performanceCommitment, setPerformanceCommitment] = useState('');
    const [followUpDate, setFollowUpDate] = useState<Date | null>(null);
    const [skillId, setSkillId] = useState<number | null>(null);
    const [behaviorId, setBehaviorId] = useState<number | null>(null);
    const [actionId, setActionId] = useState<number | null>(null);
    const [actionDescription, setActionDescription] = useState('');
    const [successCriteria, setSuccessCriteria] = useState('');
    const [notes, setNotes] = useState('');

    // Whether session data load has been attempted (set by useSessionData via this flag)
    const [sessionDataLoaded, setSessionDataLoaded] = useState(false);

    // Set default follow-up date to next Monday ONLY after session data has
    // been loaded and confirmed to have no existing follow_up_date.
    // This prevents overriding a saved date when editing an existing session.
    useEffect(() => {
        if (sessionDataLoaded && !followUpDate) {
            const today = new Date();
            const dayOfWeek = today.getDay();
            const daysUntilMonday = (8 - dayOfWeek) % 7 || 7;
            const nextMonday = new Date(today);
            nextMonday.setDate(today.getDate() + daysUntilMonday);
            setFollowUpDate(nextMonday);
        }
    }, [sessionDataLoaded]); // only re-runs when sessionDataLoaded flips to true

    return {
        performanceCommitment,
        setPerformanceCommitment,
        followUpDate,
        setFollowUpDate,
        skillId,
        setSkillId,
        behaviorId,
        setBehaviorId,
        actionId,
        setActionId,
        actionDescription,
        setActionDescription,
        successCriteria,
        setSuccessCriteria,
        notes,
        setNotes,
        sessionDataLoaded,
        setSessionDataLoaded,
    };
};

/**
 * Custom hook for fetching Action data
 */
export const useActionData = (behaviorId: number | null) => {
    const currentEmpid = useSelector(selectCurrentEmpid);

    const { data: actionsData } = useGetCoachingActionQuery({
        employeeNumber: currentEmpid,
        coachingBehaviorId: behaviorId
    }, {
        skip: !behaviorId
    });

    const coachingActions = actionsData?.data || [];

    return { coachingActions };
};

/**
 * Custom hook for loading session data.
 * Restores all fields including follow_up_date from the existing session.
 * Notifies the layout form when load is complete via setSessionDataLoaded.
 */
export const useSessionData = (
    sessionId: number | undefined,
    metricTypeName: string | undefined,
    setters: {
        setFrameNotes: (value: string) => void;
        setUnderstandNotes: (value: string) => void;
        setExploreNotes: (value: string) => void;
        setPerformanceCommitment: (value: string) => void;
        setFollowUpDate: (value: Date | null) => void;
        setSkillId: (value: number | null) => void;
        setBehaviorId: (value: number | null) => void;
        setActionId: (value: number | null) => void;
        setActionDescription: (value: string) => void;
        setSuccessCriteria: (value: string) => void;
        setNotes: (value: string) => void;
        setSessionDataLoaded: (value: boolean) => void;
    }
) => {
    const [isDataLoaded, setIsDataLoaded] = useState(false);

    const { data: sessionData } = useGetSessionByIdQuery(sessionId || 0, {
        skip: !sessionId,
    });

    useEffect(() => {
        if (sessionData?.data && !isDataLoaded) {
            const session = sessionData.data;
            setters.setFrameNotes(session.frame_notes || '');
            setters.setUnderstandNotes(session.understand_notes || '');
            setters.setExploreNotes(session.explore_notes || '');
            // DB stores percentage as decimal fraction (0–1). Multiply × 100
            // so the user sees and edits a whole-number percentage (0–100).
            const rawCommitment = session.performance_commitment ?? (session.session_number === 0 ? session.metric_target_value : null);//When creating a new CAP (Session 0), default the Performance Commitment to the metric target value, instead of 0.
            let commitmentDisplay = '';
            if (rawCommitment != null) {
                if (metricTypeName === 'Percentage') {
                    commitmentDisplay = (rawCommitment * 100).toFixed(2);
                } else {
                    commitmentDisplay = parseFloat(String(rawCommitment)).toFixed(2);
                }
            }
            setters.setPerformanceCommitment(commitmentDisplay);
            // Restore existing follow_up_date — if null the default will be
            // applied by useLayoutForm after sessionDataLoaded flips to true
            setters.setFollowUpDate(
                session.follow_up_date ? new Date(session.follow_up_date) : null
            );
            setters.setSkillId(session.coaching_skill_id || null);
            setters.setBehaviorId(session.coaching_behavior_id || null);
            setters.setActionId(session.coaching_action_id || null);
            setters.setActionDescription(session.action_description || '');
            setters.setSuccessCriteria(session.success_criteria || '');
            setters.setNotes(session.notes || '');
            setIsDataLoaded(true);
            // Signal to useLayoutForm that session load is complete
            setters.setSessionDataLoaded(true);
        } else if (!sessionId) {
            // No session (brand new plan) — trigger default date immediately
            setters.setSessionDataLoaded(true);
        }
    }, [sessionData, isDataLoaded, sessionId]);

    return { sessionData };
};

/**
 * Validation helper
 */
export const validateLayoutForm = (
    skillId: number | null,
    behaviorId: number | null,
    actionId: number | null,
    performanceCommitment: string
): boolean => {
    if (!skillId || !behaviorId || !actionId) {
        toast.error('Please complete all required fields before proceeding');
        return false;
    }

    if (!performanceCommitment.trim()) {
        toast.error('Please enter Performance Commitment');
        return false;
    }

    return true;
};

/**
 * Save action plan helper
 */
export const saveActionPlan = async (
    sessionId: number | undefined,
    coachingActionPlanId: number | undefined,
    coachingNextBestActionId: number | undefined,
    scorecardMetricId: number | undefined,
    fuelData: {
        frameNotes: string;
        understandNotes: string;
        exploreNotes: string;
    },
    layoutData: {
        skillId: number;
        behaviorId: number;
        actionId: number;
        actionDescription: string;
        successCriteria: string;
        notes: string;
        performanceCommitment: number;
        followUpDate: Date | null;
    },
    saveFuelNotesMutation: any,
    updateLayoutMutation: any
): Promise<boolean> => {
    if (!sessionId) {
        toast.error('No session ID available');
        return false;
    }

    if (!coachingActionPlanId) {
        toast.error('No action plan ID available');
        return false;
    }

    if (!scorecardMetricId) {
        toast.error('Missing metric information');
        return false;
    }

    try {
        await saveFuelNotesMutation({
            coaching_action_plan_session_id: sessionId,
            coaching_action_plan_id:         coachingActionPlanId,
            frame_notes:                     fuelData.frameNotes,
            understand_notes:                fuelData.understandNotes,
            explore_notes:                   fuelData.exploreNotes,
        }).unwrap();

        await updateLayoutMutation({
            coaching_action_plan_session_id: sessionId,
            coaching_action_plan_id:         coachingActionPlanId,
            coaching_next_best_action_id:    coachingNextBestActionId,
            scorecard_metric_id:             scorecardMetricId,
            coaching_skill_id:               layoutData.skillId,
            coaching_behavior_id:            layoutData.behaviorId,
            coaching_action_id:              layoutData.actionId,
            action_description:              layoutData.actionDescription,
            success_criteria:                layoutData.successCriteria,
            notes:                           layoutData.notes,
            performance_commitment:          layoutData.performanceCommitment,
            // Use toLocalDateString to avoid UTC offset shifting the date back one day
            follow_up_date:                  layoutData.followUpDate
                ? toLocalDateString(layoutData.followUpDate)
                : null,
        }).unwrap();

        toast.success('Action Plan started successfully!');
        return true;
    } catch (error) {
        console.error('Error starting action plan:', error);
        toast.error('Failed to start action plan');
        return false;
    }
};
